import type { SimpleEvent } from '@passionware/simple-event';
import filesize from 'filesize';
import { get } from 'lodash';
import moment from 'moment-timezone';
import {
  ExclusiveEditProvider,
  useCreateExclusiveEdit
} from 'v5/patterns/forms/exclusive-edit/ExclusiveEditContext';
import { useDiscardChangesPrompt } from 'v5/patterns/forms/unsaved-changes/useDiscardChangesPrompt';
import type { UnitTarget } from 'v5/patterns/forms/schema-form/schemaForm.types';
import { StaticUnit } from 'v5/patterns/forms/schema-form/StaticUnit';
import type { PinViewerPin } from 'v5/patterns/widgets/PinViewer/PinViewer';
import { type PinTag, PinTagsUnit, usePinTagCatalog } from './PinTagsUnit';

type SidebarPin = PinViewerPin & { tags?: PinTag[] | null };

/**
 * The details of a pin and its tags, for the card beside a pin shown in
 * `PinViewer`. `handleUpdatePin` gets the pin with its whole new list of tags.
 */
export default function MediaItemSidebar({
  selected,
  handleUpdatePin,
  editTagsSignal,
  readOnly = false,
  allowCreateTags
}: {
  selected: SidebarPin;
  handleUpdatePin(pinId: number, pin: SidebarPin): void;
  /** A target with `PIN_TAGS_UNIT_ID` opens the Tags unit in edit mode (the host's Edit tags). */
  editTagsSignal?: SimpleEvent<UnitTarget>;
  /** The tags can't be changed. */
  readOnly?: boolean;
  /** Offers "Create new tag" for a typed name. */
  allowCreateTags: boolean;
}) {
  const catalog = usePinTagCatalog();
  const discardPrompt = useDiscardChangesPrompt();
  const exclusiveEdit = useCreateExclusiveEdit(discardPrompt.confirmDiscard);

  const file = get(selected, 'file');
  const media = file || selected;
  const createdAt: string | undefined = get(media, 'created_at');
  const size: number | undefined = get(media, 'size');

  return (
    <ExclusiveEditProvider value={exclusiveEdit}>
      <div className="flex flex-col gap-2 p-4">
        <StaticUnit
          title="Name"
          value={get(media, 'name') || get(media, 'url') || ''}
        />
        <StaticUnit
          title="Pinned"
          value={createdAt ? moment(createdAt).fromNow() : ''}
        />
        <PinTagsUnit
          // A draft belongs to one pin.
          key={selected.id}
          tags={selected.tags ?? []}
          catalog={catalog}
          onSave={tags => handleUpdatePin(selected.id, { ...selected, tags })}
          editUnitSignal={editTagsSignal}
          readOnly={readOnly}
          allowCreate={allowCreateTags}
        />
        <StaticUnit
          title="Created"
          value={createdAt ? moment(createdAt).format('MMMM Do YYYY') : ''}
        />
        {file && size ? (
          <StaticUnit title="Size" value={filesize(size)} />
        ) : null}
      </div>
      {discardPrompt.dialog}
    </ExclusiveEditProvider>
  );
}
